/** @format */

import {
	AppProductReviewState,
	BVPixelResponse,
	FocusTrapService,
	selectBVPixel,
	selectProductReviewProductIdentifier
} from '@product-reviews/data-access';
import {
	BVEventObject,
	BVEventObjectName,
	BVEventObjectType,
	BVEventType,
	BVProduct
} from '../models';
import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { HmfActionTextComponent, HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { Subject, take } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { BazaarvoiceFormService } from '@product-reviews/utils';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { selectKillswitch } from '@pdp/data-access/state';

@Component({
	selector: 'product-reviews-review-button',
	templateUrl: './review-button.component.html',
	styleUrl: './review-button.component.scss',
	imports: [AsyncPipe, HmfActionTextComponent, HmfButtonComponent]
})
export class ReviewButtonComponent implements OnInit, OnDestroy {
	/**
	 * @description
	 * Button isQuestions: To reuse this component in case it is QA you can simply
	 * pass this prop to send directly to the bazaar voice modal for QA mode
	 */
	@Input() isQuestions: boolean;
	/**
	 * @description
	 * Button type: { button | link } - uses to select review button to use on layout
	 */
	@Input() type: string;
	/**
	 * @description
	 * Button label - uses to set text to display over button or link
	 */
	@Input() label: string;
	/**
	 * @description
	 * Button isDrawer - uses to set css changes to match design
	 */
	@Input() isDrawer: boolean;

	@HostBinding('style.--button-min-width.px') @Input() minWidth?: number;

	/**
	 *
	 * @description
	 * Product Id - Bazaar voice uses this identifier to display the correct reviews
	 */
	productIdentifier$ = this.store.select(selectProductReviewProductIdentifier);

	private UNSUBSCRIBE$ = new Subject<void>();
	reviewsSubmissionFormV2Enabled = this.store.selectSignal(
		selectKillswitch('reviewsSubmissionFormV2Enabled')
	);
	selectBV$ = this.store.select(selectBVPixel);
	bvPixelResponse: BVPixelResponse;

	constructor(
		private store: Store<AppProductReviewState>,
		private focusService: FocusTrapService,
		private bvFormService: BazaarvoiceFormService
	) {}

	returnElementFocus: HTMLElement;
	onLoadLastElement: HTMLElement;

	ngOnInit() {
		this.focusService.returnElement$
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((returnFocus) => {
				this.returnElementFocus = returnFocus;
			});

		this.selectBV$.pipe(take(1)).subscribe((bv: BVPixelResponse) => {
			this.bvPixelResponse = bv;
		});
	}

	ngOnDestroy() {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	/**
	 *
	 * createSubmissionEventHandler is a function that
	 * responds to bazaarvoice submission events,
	 * depending on the message an action is taken
	 *
	 * @param message This is the message obtained by
	 * according to the submission events
	 *
	 */
	createSubmissionEventHandler(message: string) {
		if (message === 'submissionClose' || message === 'submissionSubmitted') {
			this.onLoadLastElement.focus();
		}
	}

	/**
	 * @description
	 * Set trigger BazaarVoice function for Write a Review
	 * @Input
	 * Receive productId as string
	 */
	triggerWriteAReview(productIdentifier: string): void {
		const productId = productIdentifier;
		const bvObject: BVEventObject = {
			type: BVEventObjectType.USED,
			name: BVEventObjectName.WRITE,
			brand: this.bvPixelResponse.brand,
			productId: this.bvPixelResponse.productId,
			bvProduct: BVProduct.RATINGSANDREVIEWS,
			categoryId: this.bvPixelResponse.category,
			detail1: 'review',
			detail2: 'PrimaryRatingSummary'
		};

		BV.pixel.trackEvent(BVEventType.FEATURE, bvObject);

		$BV.configure('global', {
			productId,
			events: {
				submissionLoad: () => (this.onLoadLastElement = this.returnElementFocus),
				submissionClose: () => this.createSubmissionEventHandler('submissionClose'),
				submissionSubmitted: () => this.createSubmissionEventHandler('submissionSubmitted')
			}
		});


		if (this.isQuestions) {
			$BV.ui('qa', 'submit_question', { productId });
		} else if (this.reviewsSubmissionFormV2Enabled()) {
			this.bvFormService.openReviewSubmissionV2(productId);
		} else {
			$BV.ui('rr', 'submit_review', { productId });
		}
	}
}
